import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, CreditCard } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-dark-navy text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <img 
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="GBW Bodyworks Logo" 
              className="h-12 w-auto mb-4"
            />
            <p className="text-soft-grey mb-4">
              Professional car body repair and painting services in Leicestershire. Quality workmanship with color-matched perfection.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-bmw-blue">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-soft-grey hover:text-bmw-blue transition">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-soft-grey hover:text-bmw-blue transition">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-soft-grey hover:text-bmw-blue transition">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/services/scratch-paint-repair" className="text-soft-grey hover:text-bmw-blue transition">
                  Our Services
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-bmw-blue">Our Services</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/services/scratch-paint-repair" className="text-soft-grey hover:text-bmw-blue transition">
                  Scratch & Paint Repair
                </Link>
              </li>
              <li>
                <Link to="/services/dent-repair" className="text-soft-grey hover:text-bmw-blue transition">
                  Dent Repair & Removal
                </Link>
              </li>
              <li>
                <Link to="/services/alloy-wheel-repair" className="text-soft-grey hover:text-bmw-blue transition">
                  Alloy Wheel Repair
                </Link>
              </li>
              <li>
                <Link to="/services/ceramic-coating" className="text-soft-grey hover:text-bmw-blue transition">
                  Ceramic Coating
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-bmw-blue">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <MapPin size={18} className="text-bmw-blue mt-1 flex-shrink-0" />
                <span className="text-soft-grey text-sm">
                  85-87 Station Road<br />
                  Countesthorpe<br />
                  Leicestershire LE8 5TD
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={18} className="text-bmw-blue flex-shrink-0" />
                <div className="flex flex-col text-sm">
                  <a href="tel:08000452446" className="text-soft-grey hover:text-bmw-blue transition">
                    0800 045 2446
                  </a>
                  <a href="tel:08000453447" className="text-soft-grey hover:text-bmw-blue transition">
                    0800 045 3447
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <Mail size={18} className="text-bmw-blue mt-1 flex-shrink-0" />
                <div className="flex flex-col text-sm">
                  <a href="mailto:support@GBWBodyworksLimited.co.uk" className="text-soft-grey hover:text-bmw-blue transition break-all">
                    support@GBWBodyworksLimited.co.uk
                  </a>
                  <a href="mailto:help@GBWBodyworksLimited.co.uk" className="text-soft-grey hover:text-bmw-blue transition break-all">
                    help@GBWBodyworksLimited.co.uk
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <Clock size={18} className="text-bmw-blue mt-1 flex-shrink-0" />
                <div className="text-soft-grey text-sm">
                  <p>Week Days: 09:00 – 17:00</p>
                  <p>Saturday: 09:00 – 17:00</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="border-t border-dark-teal mt-8 pt-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <CreditCard className="text-bmw-blue" size={20} />
              <span className="text-soft-grey text-sm font-semibold">Secure Payment Methods</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-3 h-14 flex items-center justify-center min-w-[70px]">
                <img 
                  src="/payments/18-05-22_02_15_7-Logo_US_Standard_new_visa_RGB 1.png" 
                  alt="Visa" 
                  className="h-8 object-contain"
                />
              </div>
              <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-3 h-14 flex items-center justify-center min-w-[70px]">
                <img 
                  src="/payments/18-05-22_02_15_7-Logo_US_Standard_new_visa_RGB 2.png" 
                  alt="Mastercard" 
                  className="h-8 object-contain"
                />
              </div>
              <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-3 h-14 flex items-center justify-center min-w-[70px]">
                <img 
                  src="/payments/18-05-22_02_15_7-Logo_US_Standard_new_visa_RGB 3.png" 
                  alt="American Express" 
                  className="h-8 object-contain"
                />
              </div>
              <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-3 h-14 flex items-center justify-center min-w-[70px]">
                <img 
                  src="/payments/18-05-22_02_15_7-Logo_US_Standard_new_visa_RGB 4.png" 
                  alt="Discover" 
                  className="h-8 object-contain"
                />
              </div>
              <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-3 h-14 flex items-center justify-center min-w-[70px]">
                <img 
                  src="/payments/18-05-22_02_15_7-Logo_US_Standard_new_visa_RGB 5.png" 
                  alt="UnionPay" 
                  className="h-8 object-contain"
                />
              </div>
              <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-3 h-14 flex items-center justify-center min-w-[70px]">
                <img 
                  src="/payments/18-05-22_02_15_7-Logo_US_Standard_new_visa_RGB 6.png" 
                  alt="JCB" 
                  className="h-8 object-contain"
                />
              </div>
              <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-3 h-14 flex items-center justify-center min-w-[70px]">
                <img 
                  src="/payments/18-05-22_02_15_7-Logo_US_Standard_new_visa_RGB 7.png" 
                  alt="Diners Club" 
                  className="h-8 object-contain"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-dark-teal mt-6 pt-6 text-center">
          <p className="text-soft-grey text-sm">
            &copy; {currentYear} GBW Bodyworks Limited. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
